import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { playChime, initSoundUnlock, isMuted } from "@/lib/sound";
import { toast } from "sonner";

// Polls notifications for the signed-in user and plays a role-specific chime when new ones arrive.
export function NotificationWatcher() {
    const { user } = useAuth();
    const seen = useRef(null); // set of notification ids already known

    useEffect(() => { initSoundUnlock(); }, []);

    useEffect(() => {
        if (!user) { seen.current = null; return; }
        let timer;

        const poll = async () => {
            try {
                const { data } = await api.get("/notifications");
                const list = data || [];
                if (seen.current === null) {
                    seen.current = new Set(list.map((n) => n.id));
                    return;
                }
                const fresh = list.filter((n) => !seen.current.has(n.id));
                if (fresh.length) {
                    fresh.forEach((n) => seen.current.add(n.id));
                    const emergency = fresh.some((n) => /emergency|🚨/i.test(n.title || "") || /emergency/i.test(n.body || ""));
                    const isCare = fresh[0]?.mode === "care";
                    const kind = user.role === "professional" || user.role === "admin" ? "services" : (isCare ? "care" : "services");
                    const role = user.role === "admin" ? "admin" : user.role === "professional" ? "professional" : (isCare ? "care" : "customer");
                    playChime(role, emergency || user.role === "admin", kind);
                    if (!isMuted(kind)) {
                        const n = fresh[0];
                        toast(n.title, { description: n.body, duration: emergency ? 10000 : 5000 });
                    }
                }
            } catch (e) { /* ignore */ }
        };

        poll();
        timer = setInterval(poll, 20000);
        return () => { clearInterval(timer); };
    }, [user?.id, user?.role]);

    return null;
}
